import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import "./monthlyMIS.css";

const API_URL = process.env.REACT_APP_API_URL || "https://api.transtrackglobal.in";

const MonthlyMIS = () => {
  const [recruiters, setRecruiters] = useState([]);
  const [clients, setClients] = useState([]);
  const [domains, setDomains] = useState([]);
  const [misData, setMisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const userType = localStorage.getItem("userType") || "admin";

  const [filters, setFilters] = useState({
    year: "",
    month: "",
    recruiter: "",
    domain: "",
    status: "",
    client: "",
  });

  /* -------------------- LOAD DROPDOWNS -------------------- */
  useEffect(() => {
    const fetchRecruiters = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/recruiterName`);
        setRecruiters(res.data);
      } catch (error) {
        console.error("Failed to load recruiters", error);
      }
    };

    const fetchClients = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/clientName`);
        setClients(res.data);
      } catch (error) {
        console.error("Failed to load clients", error);
      }
    };

    const fetchDomains = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/domains`);
        setDomains(res.data);
      } catch (error) {
        console.error("Failed to load domains", error);
      }
    };

    fetchRecruiters();
    fetchClients();
    fetchDomains();
  }, []);

  /* -------------------- STATIC DATA -------------------- */
  const years = ["2025", "2026"];
  const months = [
    { label: "January", value: 1 },
    { label: "February", value: 2 },
    { label: "March", value: 3 },
    { label: "April", value: 4 },
    { label: "May", value: 5 },
    { label: "June", value: 6 },
    { label: "July", value: 7 },
    { label: "August", value: 8 },
    { label: "September", value: 9 },
    { label: "October", value: 10 },
    { label: "November", value: 11 },
    { label: "December", value: 12 },
  ];

  const statuses = ["Active", "Inactive"];

  /* -------------------- HANDLERS -------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {};
    if (filters.year) payload.year = Number(filters.year);
    if (filters.month) payload.month = Number(filters.month);
    if (filters.recruiter) payload.recruiter = filters.recruiter;
    if (filters.domain) payload.domain = filters.domain;
    if (filters.status) payload.status = filters.status;
    if (filters.client) payload.client = filters.client;

    const res = await axios.post(
      `${API_URL}/api/monthlymis`,
      payload,
    );

    setMisData(res.data);
    setLoading(false);
  };

  /* -------------------- EXCEL EXPORT -------------------- */
  const exportToExcel = () => {
    if (!misData) return;

    const exportData = [
      {
        "Total Resources": misData.totalResources ?? 0,
        "Active Resources": misData.activeResources,
        "New Joinees": misData.newJoinees,
        Exits: misData.exitsThisMonth,
        "Total Monthly Margin": misData.totalMonthlyMargin,
        " Monthly Average Margin %": misData.averageMarginPct,
        "Domains Active": misData.domainsActive,
        "Upcoming WO Renewals This Month": misData.upcomingRenewals,
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly MIS");

    XLSX.writeFile(workbook, "Monthly_MIS_Report.xlsx");
  };

  /* -------------------- JSX -------------------- */
  return (
    <div className="mis-page">
      {/* HEADER */}
      <div className="mis-header">
        <h3 className="mis-title">Monthly MIS Report</h3>
        <Link to="/dashboard" className="btn outline-btn">
          <i className="bi bi-arrow-left"></i> Dashboard
        </Link>
      </div>

      {/* FILTER CARD */}
      <form className="mis-card" onSubmit={handleSubmit}>
        {/* 🔹 CARD HEADER */}
        <div className="mis-card-header">
          <h5>Filters</h5>
          <button type="submit" className="btn gradient-btn">
            <i className="bi bi-bar-chart-line me-1"></i> Generate
          </button>
        </div>

        {/* 🔹 FILTER GRID */}
        <div className="mis-grid">
          {/* Year */}
          <div className="input-3d">
            <label>Year</label>
            <select name="year" value={filters.year} onChange={handleChange}>
              <option value="">Select</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {/* Month */}
          <div className="input-3d">
            <label>Month</label>
            <select name="month" value={filters.month} onChange={handleChange}>
              <option value="">Select</option>
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* Client */}
          <div className="input-3d">
            <label>Client</label>
            <select
              name="client"
              value={filters.client}
              onChange={handleChange}
            >
              <option value="">Select</option>
              {clients.map((c) => (
                <option key={c.client} value={c.client}>
                  {c.client}
                </option>
              ))}
            </select>
          </div>

          {/* Recruiter */}
          <div className="input-3d">
            <label>Recruiter</label>
            <select
              name="recruiter"
              value={filters.recruiter}
              onChange={handleChange}
            >
              <option value="">Select</option>
              {recruiters.map((r) => (
                <option key={r.recruiterName} value={r.recruiterName}>
                  {r.recruiterName}
                </option>
              ))}
            </select>
          </div>

          {/* Domain */}
          <div className="input-3d">
            <label>Domain</label>
            <select
              name="domain"
              value={filters.domain}
              onChange={handleChange}
            >
              <option value="">Select</option>
              {domains.map((d) => (
                <option key={d._id} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div className="input-3d">
            <label>Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleChange}
            >
              <option value="">Select</option>
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
      </form>

      {/* EXPORT BUTTON (ONLY AFTER RESPONSE) */}
      {misData && (
        <div style={{ margin: "15px 0", textAlign: "right" }}>
          <button className="btn gradient-btn" onClick={exportToExcel}>
            <i className="bi bi-file-earmark-excel"></i> Export Excel
          </button>
        </div>
      )}

      {/* ================= MIS TABLE ================= */}
      {loading && <p>Loading MIS...</p>}

      {misData && (
        <div className="mis-card">
          <div className="table-scroll-wrapper">
            <table className="resource-table mis-table">
              <thead>
                <tr>
                  <th>Total Resources</th>
                  <th>Active Resources</th>
                  <th>New Joinees</th>
                  <th>Exits</th>
                  <th>Total Monthly Margin</th>
                  <th>Average Margin %</th>
                  <th>Domains Active</th>
                  <th>Upcoming WO Renewals This Month</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>{misData.totalResources ?? 0}</td>
                  <td>{misData.activeResources}</td>
                  <td>{misData.newJoinees}</td>
                  <td>{misData.exitsThisMonth}</td>
                  <td>₹ {misData.totalMonthlyMargin?.toLocaleString()}</td>
                  <td>{misData.averageMarginPct}%</td>
                  <td>{misData.domainsActive}</td>
                  <td>{misData.upcomingRenewals}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyMIS;
